import React, { useState } from "react";
import { aiClearHistory } from "../../api/api";
import { clearSkillMetrics } from "../../state/ai/runMetrics";
import useApi from "../../hooks/useApi";
import { AppRoutes } from "../../routes/AppRouter";
import { abortRun } from "../../state/ai/runRegistry";
import { useChatHistory } from "../../state/api/ai/useChatHistory";
import { useSkills } from "../../state/api/ai/useSkills";
import { Doc, INTERACTION_TYPE, InteractionTypeMap, Skill } from "../../types";
import RunAnalysisButton from "../components/ai/analysis/RunAnalysisButton";
import Chat from "../components/ai/chat/Chat";
import ExportChatButton from "../components/ai/chat/ExportChatButton";
import ClearChatHistoryButton from "../components/ai/chat/RemoveHistoryButton";
import DealAnalysis from "../components/ai/DealAnalysis";
import Button from "../components/buttons/Button";
import Page from "../components/container/Page";
import Icon from "../components/icons/Icon";
import LoadingSpinner from "../components/loader/LoadingSpinner";
import PageLoader from "../components/loader/PageLoader";
import Pill from "../components/pill/Pill";
import Tabs, { Tab } from "../components/tabs/Tabs";

interface SkillCardProps {
  skill: Doc<Skill>;
  onSelect: (skill: string) => void;
}

function SkillCard(props: SkillCardProps) {

  const { skill, onSelect } = props;

  const { chatHistory, isLoading, mutate } = useChatHistory({ skill: skill._id });

  const callApi = useApi();

  const [expanded, setExpanded] = useState<boolean>(false);

  const messageCount = chatHistory?.length ?? 0;
  const hasHistory = !!messageCount;

  const startNew = async () => {
    abortRun(skill._id);

    clearSkillMetrics(skill._id);

    const res = await callApi(aiClearHistory({
      skill: skill._id
    }));

    if (!res.success) return;

    await mutate(_ => [], { revalidate: false });

    onSelect(skill._id);
  }

  return (
    <div className="card w-100 p-2 d-flex flex-row justify-content-between gap-5">
      <div className="d-flex flex-column gap-1 w-100">
        <div className="d-flex flex-row justify-content-between w-100 gap-1">
          <div className="d-flex flex-column gap-1 w-100">
            <div
              className="d-flex flex-row align-items-center gap-1"
              style={{ cursor: "pointer" }}
              onClick={() => setExpanded(!expanded)}
            >
              <Icon icon={expanded ? "chevron-down" : "chevron-right"} />
              <h6 className="m-0 p-0">{skill.label ?? skill.skill_id}</h6>
            </div>
            <div className="d-flex flex-row flex-wrap gap-1">
              {
                skill.inTesting && <Pill background="var(--bs-dark)" foreground="white">In Testing</Pill>
              }
              {
                skill.fileInputRequired && <Pill background="var(--primaryAccent)" foreground="white">File Upload</Pill>
              }
              {
                hasHistory && (
                  <Pill background="var(--bs-secondary)" foreground="white">
                    {`${messageCount} message${messageCount === 1 ? "" : "s"}`}
                  </Pill>
                )
              }
            </div>
          </div>
          {
            isLoading
              ? <LoadingSpinner size={16} />
              : (
                <div className="d-flex flex-row gap-1 align-items-start">
                  {
                    hasHistory && (
                      <Button
                        variant="ghost"
                        onClick={() => onSelect(skill._id)}
                        text="Resume"
                      />
                    )
                  }
                  <Button
                    variant="subtle"
                    onClick={startNew}
                    text={hasHistory ? "Start new" : "Start now"}
                  />
                </div>
              )
          }
        </div>
        {
          expanded && (
            <span className="font-code font-s">{skill.description ?? "No description provided."}</span>
          )
        }
      </div>
    </div>
  )
}

export default function ChatPage() {

  const {
    loadingSkills,
    skillsById,
    skillsByType
  } = useSkills();

  const [type, setType] = useState<INTERACTION_TYPE>(INTERACTION_TYPE.ANALYSIS);

  const [selectedSkills, setSelectedSkills] = React.useState<Record<any, any>>({});

  const resolveSkill = React.useCallback((t: INTERACTION_TYPE): string => {
    const existing = selectedSkills?.[t];
    if (existing && skillsById?.[existing]) return existing;
    if (t !== INTERACTION_TYPE.ANALYSIS) return "";
    const forType = skillsByType?.[t] ?? [];
    if (forType.length !== 1) return "";
    return forType[0]?._id ?? "";
  }, [selectedSkills, skillsById, skillsByType]);

  const skill = resolveSkill(type);
  const usedSkill = skillsById?.[skill];

  const setChatSkill = React.useCallback((t: INTERACTION_TYPE, s: string) => {
    setSelectedSkills((prev) => ({ ...prev, [t]: s ?? "" }));
  }, []);

  if (loadingSkills) return (
    <PageLoader />
  )

  const t = (i: INTERACTION_TYPE) => {

    const x = selectedSkills?.[i];
    const s = x ? skillsById?.[x] : undefined;
    const t = skillsByType?.[i];

    if (!t?.length) {
      return null;
    }

    return (
      <Tab value={i} key={i}>
        <div className="d-flex flex-column gap-1 align-items-center">
          {InteractionTypeMap[i]}
          <Pill background="var(--primaryAccent)" foreground="white">{s?.label}</Pill>
        </div>
      </Tab>
    )
  }

  const availableSkills = skillsByType?.[type] ?? [];

  return (
    <Page
      title={AppRoutes.AI.Label}
      subtitle={AppRoutes.AI.Subtitle}
      actions={(
        <>
          <ExportChatButton skill={skill} skillMeta={usedSkill} />
          {
            type === INTERACTION_TYPE.ANALYSIS
              ? (
                <RunAnalysisButton skill={skill} />
              )
              : (
                <ClearChatHistoryButton
                  skill={usedSkill?._id}
                  onDelete={
                    1 < availableSkills?.length
                      ? () => setChatSkill(type, "")
                      : undefined
                  }
                />
              )
          }
        </>
      )}
    >
      <div className="d-flex flex-column h-100 w-100">
        <Tabs
          size="small"
          active={type}
          onClick={(x: INTERACTION_TYPE) => {
            setType(x)

          }}
        >
          {
            Object.values(INTERACTION_TYPE).map(t)
          }
        </Tabs>
        {
          usedSkill
            ? (
              type === INTERACTION_TYPE.ANALYSIS
                ? <DealAnalysis key={skill} skill={skill} skillMeta={usedSkill} />
                : <Chat key={skill} skill={skill} skillMeta={usedSkill} />
            )
            : (
              (
                type === INTERACTION_TYPE.ANALYSIS
                  ? (
                    <div className="ps-sm-4 pe-sm-4 pe-3 ps-3 pt-4">
                      <div className="d-flex flex-column gap-2">
                        <h6 className="text-muted">
                          No existing analysis
                        </h6>
                        <RunAnalysisButton skill={skill} />
                      </div>
                    </div>
                  )
                  : (
                    <div className="d-flex p-3 w-100 pt-5 align-items-start justify-content-center" style={{ flexGrow: 1, minHeight: 0, overflowY: "auto" }}>
                      {
                        availableSkills?.length
                          ? (
                            <div className="d-flex flex-column align-items-center gap-4 w-100">
                              <h4>Select a tool to continue</h4>
                              <div className="d-flex flex-column gap-2 w-100">
                                {
                                  availableSkills.map(s => (
                                    <SkillCard
                                      key={s._id}
                                      skill={s}
                                      onSelect={(id) => setChatSkill(type, id)}
                                    />
                                  ))
                                }
                              </div>
                            </div>
                          )
                          : (
                            <h5>
                              No possible options available right now
                            </h5>
                          )
                      }
                    </div>
                  )
              )
            )
        }
      </div>
    </Page >
  )
}